import { ConversationCard } from "@app/components/assistant/conversation/cards/ConversationCard";
import type { DocumentTone } from "@app/components/assistant/conversation/cards/documentTone";
import { getFileTypeIcon } from "@app/lib/file_icon_utils";
import {
  frameSlideshowContentType,
  getFrameDisplayTitle,
  isInteractiveContentType,
  isPdfContentType,
  normalizeMimeType,
} from "@app/types/files";
import { ActionFrame, Button, Download01 } from "@dust-tt/sparkle";

type ArtifactKindLabel = ReturnType<typeof getArtifactKindLabel>;

const artifactKindTone: Partial<Record<ArtifactKindLabel, DocumentTone>> = {
  Document: "doc",
  Spreadsheet: "sheet",
  Presentation: "slides",
  PDF: "pdf",
};

export function getArtifactTone(
  contentType: string,
  title: string
): DocumentTone | undefined {
  return artifactKindTone[getArtifactKindLabel(contentType, title)];
}

export function getArtifactKindLabel(contentType: string, title: string) {
  const mimeType = normalizeMimeType(contentType);
  const extension = title.split(".").pop()?.toLowerCase() ?? "";
  if (mimeType === frameSlideshowContentType) {
    return "Presentation";
  }
  if (isInteractiveContentType(mimeType)) {
    return "Frame";
  }
  if (isPdfContentType(mimeType)) {
    return "PDF";
  }
  if (
    mimeType.includes("presentation") ||
    ["ppt", "pptx", "key"].includes(extension)
  ) {
    return "Presentation";
  }
  if (
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv" ||
    ["csv", "xls", "xlsx", "tsv"].includes(extension)
  ) {
    return "Spreadsheet";
  }
  if (mimeType === "text/html") {
    return "Web page";
  }
  if (
    mimeType.includes("wordprocessing") ||
    mimeType === "text/markdown" ||
    ["doc", "docx", "md", "txt"].includes(extension)
  ) {
    return "Document";
  }
  return "File";
}

interface GeneratedArtifactCardProps {
  title: string;
  contentType: string;
  onOpen: () => void;
  downloadUrl?: string;
}

/**
 * A deliverable the agent produced. It reads as the outcome of the turn, and its full view
 * lives in the 3rd pane — the card only says what it is and gets you there.
 */
export function GeneratedArtifactCard({
  title,
  contentType,
  onOpen,
  downloadUrl,
}: GeneratedArtifactCardProps) {
  const isFrame = isInteractiveContentType(normalizeMimeType(contentType));
  const visual = isFrame ? ActionFrame : getFileTypeIcon(contentType, title);
  const kindLabel = getArtifactKindLabel(contentType, title);
  const displayTitle = isFrame ? getFrameDisplayTitle(title) : title;

  return (
    <ConversationCard
      visual={visual}
      tone={getArtifactTone(contentType, title)}
      onOpen={onOpen}
      className="w-fit min-w-72"
      openLabel={`Open ${displayTitle}`}
      eyebrow={kindLabel}
      title={<span className="truncate">{displayTitle}</span>}
      meta="Opens in the side panel"
      headerActions={
        downloadUrl && (
          <Button
            variant="ghost"
            size="xs"
            icon={Download01}
            tooltip="Download"
            aria-label="Download"
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
          />
        )
      }
    />
  );
}
